<template>
  <Block :badge-class="statusClassBg" :emoji="displayEmoji" :title="displayTitle">
    <template #subtitle>
      <h3 class="text-base font-medium tracking-tight text-gray-600">{{ displaySubtitle }}</h3>
    </template>
    <!-- Table -->
    <div class="overflow-x-auto max-h-48 overflow-y-auto" ref="tableContainer">
      <Table :columns="columns" :data="formattedStoreRanking">
        <template #rank="{ row }">
          <div
            class="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs"
            :class="row.isCurrent ? 'bg-purple-200 text-purple-800' : getMedalClass(row.rank)"
          >
            {{ row.rank }}
          </div>
        </template>

        <template #salesChannel="{ row }">
          <h4 class="text-sm font-semibold" :class="row.isCurrent ? 'text-purple-700' : 'text-gray-900'">
            {{ row.salesChannel }}
          </h4>
        </template>

        <template #sales="{ row }">
          <p class="text-sm font-bold" :class="row.isCurrent ? 'text-purple-700' : 'text-gray-600'">
            {{ $t("roadmap.sales", row.sales) }}
          </p>
        </template>
      </Table>
    </div>
  </Block>
</template>

<script>
import { Table, Block, computeCompetitionRanking } from "@estaly/ui";
import { scrollToCurrentItem } from "@/utils/scroller";

export default {
  components: {
    Table,
    Block,
  },
  props: {
    storeRanking: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    emoji: {
      type: String,
      default: "",
    },
  },
  computed: {
    formattedStoreRanking() {
      return computeCompetitionRanking(this.storeRanking, "salesCount").map((store) => {
        const isCurrent = store.current || false;
        return {
          rank: store.rank,
          salesChannel: store.salesChannel,
          sales: store.salesCount,
          leadsWaiting: store.leadsWaitingForConfirmation || 0,
          isCurrent,
          rowClass: isCurrent ? "bg-purple-50" : "",
        };
      });
    },
    currentStorePosition() {
      return this.currentStore?.rank || 0;
    },
    currentStore() {
      return this.storeRanking.find((store) => store.current);
    },
    totalStores() {
      return this.storeRanking.length;
    },
    statusEmoji() {
      if (this.currentStorePosition <= 3) return "🏆";
      if (this.currentStorePosition <= 10) return "🔝";
      return "🌱";
    },
    statusClassBg() {
      if (this.currentStorePosition <= 3) return "bg-green-50";
      if (this.currentStorePosition <= 10) return "bg-yellow-50";
      return "bg-gray-50";
    },
    storeRankingTitle() {
      return `${this.currentStorePosition}ᵉ / ${this.totalStores} magasins ce mois`;
    },
    storeToBeat() {
      const { storeRanking, currentStorePosition, currentStore } = this;

      if (!storeRanking?.length || !currentStore) return null;

      // The first store must beat the second one.
      if (currentStorePosition === 1) {
        return storeRanking.find((store) => store.rank === 2) || null;
      }

      // Otherwise, we search the best rank above, having more sales than the current store
      for (let rank = currentStorePosition - 1; rank >= 1; rank--) {
        const candidate = storeRanking.find((store) => store.rank === rank);
        if (candidate && candidate.salesCount > currentStore.salesCount) {
          return candidate;
        }
      }

      return null;
    },
    salesToBeatText() {
      if (!this.storeToBeat) return "Classement réseau";
      if (this.currentStorePosition === 1)
        return `${this.storeToBeat.salesChannel} derrière avec ${this.$t("roadmap.sales", this.storeToBeat.salesCount)}`;
      const salesDifference = this.storeToBeat.salesCount - this.currentStore.salesCount;

      return salesDifference > 0
        ? `${this.$t("roadmap.sales", salesDifference)} pour battre ${this.storeToBeat.salesChannel}`
        : `Égalité avec ${this.storeToBeat.salesChannel}`;
    },
    displayTitle() {
      return this.title || this.storeRankingTitle;
    },
    displaySubtitle() {
      return this.subtitle || this.salesToBeatText;
    },
    displayEmoji() {
      return this.emoji || this.statusEmoji;
    },
    columns() {
      return [
        {
          label: null,
          field: "rank",
          slot: "rank",
          widthClass: "w-12",
        },
        {
          label: null,
          field: "salesChannel",
          slot: "salesChannel",
        },
        {
          label: null,
          field: "sales",
          slot: "sales",
          numeric: true,
          widthClass: "w-24",
        },
      ];
    },
  },
  mounted() {
    this.scrollToCurrentStore();
  },
  watch: {
    formattedStoreRanking() {
      this.$nextTick(() => {
        this.scrollToCurrentStore();
      });
    },
  },
  methods: {
    getMedalClass(ranking) {
      if (ranking === 0) return;

      const classes = [
        "bg-linear-to-r/srgb from-yellow-400 to-yellow-500 text-white",
        "bg-linear-to-r/srgb from-gray-300 to-gray-400 text-white",
        "bg-linear-to-r/srgb from-orange-400 to-orange-800 text-white",
      ];
      return classes[ranking - 1] || "bg-gray-100 text-gray-600";
    },
    scrollToCurrentStore() {
      this.$nextTick(() => {
        if (!this.$refs.tableContainer) return;
        scrollToCurrentItem(this.$refs.tableContainer, this.formattedStoreRanking, (store) => store.isCurrent);
      });
    },
  },
};
</script>
